import React, { useCallback, useEffect, useState, useRef } from "react";
import Select from "react-select";
import { connect } from "react-redux";
import { useParams, Link } from "react-router";
import { DebounceInput } from "react-debounce-input";
import { createColumnHelper } from "@tanstack/react-table";

import {
  emptyString,
  pathConstant,
  loginConstants,
  sessionStorageKey,
  brandProfileConstants,
  MENU_PLACEMENT,
  viewPathConstants,
  FILTER_LABEL,
} from "../../constants";
import { getItemFromStorage, setItemToStorage } from "../../../../system";
import {
  useSpinner,
  Option,
  Spinner,
  DataTable,
  SurveyStats,
  ValueContainer,
  UTCToDateFormat,
  loadingIndicator,
  paginatedPostRequestQueryBody,
  isSuperAdmin,
  isSuperOrBrandAdmin,
} from "../../../../shared";
import {
  brandProfileActions,
  individualStoreProfileActions,
} from "../../redux/actions";
import ImportOrder from "../../../../shared/components/ImportOrder/ImportOrder";

const {
  OR,
  AND,
  NAME,
  ROLE,
  STATE,
  USER_ROLE,
  GROUP_NAME,
  LOCATION_STATE,
  GET_BRAND_LOCATIONS,
  FETCH_BRAND_RESTAURANT,
} = brandProfileConstants;

//Brand Profile
const BrandProfile = (props) => {
  const {
    brandProfileReducer: {
      stores,
      brandData,
      brandProfile,
      collectiveData,
      brandDataPagination,
    },
    getBrandId,
    getBrandProfile,
    getBrandRestaurants,
    resetRestaurantProfile,
  } = props;

  const { brandId } = useParams();

  const filters = useRef({
    name: [],
    state: [],
    "group.name": [],
  });

  //Initial states
  const [searchString, setSearchString] = useState(emptyString);
  const [sorting, setSorting] = useState({ id: "name", desc: true });
  const [filteredDataValue, setFilteredDataValue] = useState(emptyString);

  //Handle click store name
  const handleClickStoreName = (restaurantData) => {
    setItemToStorage(loginConstants.LOCATION_ID, restaurantData.id);
    setItemToStorage(
      LOCATION_STATE,
      JSON.stringify({
        name: isSuperAdmin()
          ? pathConstant.STORE_BRAND_PROFILE
          : pathConstant.BRAND_PROFILE,
      })
    );
  };

  const cellNameRenderer = useCallback(
    ({ row }) => {
      const restaurantData = row.original;
      return restaurantData.name ? (
        <Link
          className="table-links"
          to={`${
            isSuperOrBrandAdmin() ? viewPathConstants.STORES : emptyString
          }/${pathConstant.BRAND_PROFILE}/${brandId}/${
            pathConstant.STORE_PERFORMANCE
          }/${restaurantData.id}`}
          state={{
            name: isSuperAdmin()
              ? pathConstant.STORE_BRAND_PROFILE
              : pathConstant.BRAND_PROFILE,
          }}
          onClick={() => handleClickStoreName(restaurantData)}
        >
          {restaurantData.name}
        </Link>
      ) : (
        <span />
      );
    },
    // eslint-disable-next-line
    [brandId]
  );

  const cellGroupNameRenderer = useCallback(
    ({ row }) => {
      const groupData = row.original.group;

      if (groupData?.name) {
        return (
          <Link
            className="table-links"
            to={`${
              isSuperOrBrandAdmin() ? viewPathConstants.STORES : emptyString
            }/${pathConstant.BRAND_PROFILE}/${brandId}${
              viewPathConstants.GROUP_PERFORMANCE_PAGE
            }/${groupData.id}`}
            state={{
              name: isSuperAdmin()
                ? pathConstant.STORE_BRAND_PROFILE
                : pathConstant.BRAND_PROFILE,
            }}
            onClick={() =>
              setItemToStorage(sessionStorageKey.FRANCHISEE__ID, groupData.id)
            }
          >
            {groupData.name}
          </Link>
        );
      }
      return <span>-</span>;
    },
    // eslint-disable-next-line
    [brandId]
  );

  //Static headers for data table
  //"Cell" is being used to manipulate the data for displaying purpose
  const columnHelper = createColumnHelper();
  const brandRestaurantsColumns = [
    columnHelper.accessor("name", {
      header: () => "STORE NAME",
      cell: cellNameRenderer,
    }),

    columnHelper.accessor("createdAt", {
      header: () => "DATE ACTIVATED",
      cell: (row) => {
        const restaurantData = row.row.original;
        return restaurantData.createdAt
          ? UTCToDateFormat(restaurantData.createdAt)
          : "";
      },
    }),

    columnHelper.accessor("totalResponses", {
      header: () => "TOTAL RESPONSES",
      enableSorting: false,
    }),

    columnHelper.accessor("group.name", {
      header: () => "GROUP NAME",
      cell: cellGroupNameRenderer,
    }),
  ];

  //Create query string for filters
  const generateQuery = (filterObj) => {
    //Create array of filters name from filters object e.g(['name','state','ownerName'])
    const filterNames = Object.keys(filterObj);
    const queryArray = filterNames.reduce((arr, name) => {
      const values = filterObj[name];
      //If multiple filter apply inside one filter then return with OR else only return one value e.g(['name="Monal Resto"']) or (['name="test 1" OR name="test 2"])
      if (values.length) {
        const filterValues = values.map((value) => {
          // Escape double quotes in the values to avoid breaking the query
          const escapedValue = value.replace(/"/g, '\\"');
          return `${name}.Trim()="${escapedValue}"`;
        });
        arr.push(`(${filterValues.join(OR)})`); // Surround filter values with parentheses
      }
      return arr;
    }, []);

    //If we select multiple filters e.g(state, name and ownerName) then each filter will be separated with AND e.g(['name="test 1" AND state="state 1" AND etc...])
    return queryArray.join(AND);
  };

  //Set filter for the select dropdown
  const setFilteredData = (property, value) => {
    //Save values against its filter name
    if (property === NAME) {
      filters.current.name = value;
    } else if (property === STATE) {
      filters.current.state = value;
    } else {
      filters.current[GROUP_NAME] = value;
    }
    const query = generateQuery(filters.current);
    setFilteredDataValue(query);
  };

  // Show loading while a request is running.
  const spinner = useSpinner();

  // set pagination details with request body
  const getPaginatedPostRequestBody = (pageNumber, sortBy) => {
    const requestBody = paginatedPostRequestQueryBody(
      brandDataPagination,
      pageNumber,
      searchString,
      sortBy || sorting,
      filteredDataValue || emptyString,
      emptyString
    );
    if (sortBy) {
      setSorting(sortBy);
    }

    return requestBody;
  };

  // get table data with pagination
  const getData = (pageNumber, sortBy) => {
    const requestBody = getPaginatedPostRequestBody(pageNumber, sortBy);
    requestBody.orderBy = requestBody.orderBy.replaceAll("_", ".");
    getBrandRestaurants(brandId, requestBody);
  };

  //Set unique states
  const uniqueStates = [...new Set(stores.map((loc) => loc.state))];

  //Set unique group name
  const uniqueFranchiseName = [
    ...new Set(
      stores.map((f) => f?.group?.name).filter((name) => name !== undefined)
    ),
  ];

  //Styling react select
  const customStyle = {
    //Changing default style of options in react select
    option: (baseStyles, state) => ({
      ...baseStyles,
      backgroundColor: state.isSelected ? "transparent" : "auto",
      color: "black",
    }),
  };

  //Fetch data from these calls before the page loads
  useEffect(() => {
    props.getBrandLocationsByBrandId(brandId);
    setItemToStorage("BrandId", brandId);
    resetRestaurantProfile();
    // eslint-disable-next-line
  }, []);

  // fetch data from these calls before the page loads
  useEffect(() => {
    getBrandId(brandId);
    let role = getItemFromStorage(ROLE);
    if (role !== USER_ROLE) {
      getBrandProfile(brandId);
    }
    getData(0);
    // eslint-disable-next-line
  }, [searchString, filteredDataValue]);

  return (
    <>
      {spinner(FETCH_BRAND_RESTAURANT) && <Spinner />}
      <div className="container-fluid py-3">
        <div className="notification-form card shadow ">
          <div className="card-body p-0">
            <div className="row">
              <div className="col-6 m-0">
                <h5 className="ps-4 mb-0 py-3">General</h5>
              </div>
              <div className="col-6 d-none">
                <ImportOrder />
              </div>
            </div>
            <div className="row border-top">
              <div className="col-12">
                <div className="row px-4 p-3">
                  <div className="col-12 d-flex" data-testid="brand-name">
                    <p className="col-lg-2 col-md-3 col-6 survey_profile_labels bold-5">
                      Brand Name:
                    </p>
                    <span className="profile_lable_values font-14-px">
                      {brandProfile.name}
                    </span>
                  </div>
                  <div className="col-12 d-flex" data-testid="brand-id">
                    <p className="col-lg-2 col-md-3 col-6 survey_profile_labels bold-5">
                      Brand ID:
                    </p>
                    <span className="profile_lable_values font-14-px">
                      {brandProfile.id}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="notification-form card mt-3 shadow">
          <div className="card-body p-0">
            <div className="row">
              <div className="col-6 py-3">
                <h5 className="ps-4 m-0" data-testid="stores-table-heading">
                  Stores
                </h5>
              </div>
              <div className="col-6 py-3 ">
                <div className="search-container ms-auto me-4">
                  <DebounceInput
                    debounceTimeout={1000}
                    type="text"
                    className=" search-restaurants"
                    placeholder="Search"
                    id="search-store-locations"
                    value={searchString}
                    onChange={(e) => setSearchString(e.target.value)}
                  />
                  <i className="fa fa-search search-icon" aria-hidden="true" />
                </div>
              </div>
            </div>

            <hr className="mt-0" />

            <div className="row px-4 pt-2 pb-2" data-testid="filters-section">
              <div className="col-12 col-md-4">
                <Select
                  name="name"
                  options={[...stores]}
                  components={{
                    IndicatorSeparator: () => null,
                    Option,
                    ValueContainer: ValueContainer,
                    LoadingIndicator: loadingIndicator,
                  }}
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.name}
                  onChange={(selectedOption) =>
                    setFilteredData(
                      NAME,
                      selectedOption.map((opt) => opt.name)
                    )
                  }
                  placeholder={FILTER_LABEL.ALL_STORES}
                  isSearchable
                  maxMenuHeight={200}
                  menuPlacement={MENU_PLACEMENT.AUTO}
                  styles={customStyle}
                  isDisabled={spinner(GET_BRAND_LOCATIONS)}
                  isLoading={spinner(GET_BRAND_LOCATIONS)}
                />
              </div>
              <div className="col-12 col-md-4 py-md-0 py-2 ">
                <Select
                  name="state"
                  options={[
                    ...uniqueStates
                      .filter((state) => state !== emptyString)
                      .map((state) => ({ id: state, state })),
                  ]}
                  components={{
                    IndicatorSeparator: () => null,
                    Option,
                    ValueContainer: ValueContainer,
                    LoadingIndicator: loadingIndicator,
                  }}
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  getOptionValue={(option) => option.id}
                  getOptionLabel={(option) => option.state}
                  onChange={(selectedOption) =>
                    setFilteredData(
                      STATE,
                      selectedOption.map((opt) => opt.state)
                    )
                  }
                  placeholder={FILTER_LABEL.ALL_STATES}
                  isSearchable
                  maxMenuHeight={200}
                  menuPlacement={MENU_PLACEMENT.AUTO}
                  styles={customStyle}
                  isDisabled={spinner(GET_BRAND_LOCATIONS)}
                  isLoading={spinner(GET_BRAND_LOCATIONS)}
                />
              </div>
              <div className="col-12 col-md-4">
                <Select
                  name="frnachiseName"
                  options={[
                    ...uniqueFranchiseName.map((group) => ({
                      id: group,
                      name: group,
                    })),
                  ]}
                  components={{
                    IndicatorSeparator: () => null,
                    Option,
                    ValueContainer: ValueContainer,
                    LoadingIndicator: loadingIndicator,
                  }}
                  isMulti
                  hideSelectedOptions={false}
                  closeMenuOnSelect={false}
                  getOptionValue={(opt) => opt.id}
                  getOptionLabel={(opt) => opt.name}
                  onChange={(selectedOption) =>
                    setFilteredData(
                      GROUP_NAME,
                      selectedOption.map((opt) => opt.name)
                    )
                  }
                  placeholder={FILTER_LABEL.ALL_GROUPS}
                  isSearchable
                  maxMenuHeight={200}
                  menuPlacement={MENU_PLACEMENT.AUTO}
                  styles={customStyle}
                  isDisabled={spinner(GET_BRAND_LOCATIONS)}
                  isLoading={spinner(GET_BRAND_LOCATIONS)}
                />
              </div>
            </div>

            <SurveyStats
              total={collectiveData.totalSurvey}
              positive={collectiveData.positiveResponses}
              negative={collectiveData.negativeResponses}
            />
            <div>
              <DataTable
                columns={brandRestaurantsColumns}
                data={brandData}
                totalRows={brandDataPagination.totalElements}
                numberOfRowsPerPage={brandDataPagination.size}
                pageNumber={brandDataPagination.number + 1}
                onPageChange={(pageNum) => getData(pageNum)}
                onColumnSort={(sortBy) =>
                  getData(brandDataPagination.number, sortBy)
                }
                sortedBy={sorting}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

const mapStateToProps = (state) => ({
  authentication: state.authentication.user,
  brandProfileReducer: state.brandProfileReducer,
});

const mapDispatchToProps = (dispatch) => ({
  getBrandProfile: (brandId) =>
    dispatch(brandProfileActions.getBrandProfile(brandId)),
  getBrandRestaurants: (brandId, body) =>
    dispatch(brandProfileActions.getBrandRestaurants(brandId, body)),
  getBrandLocationsByBrandId: (brandId) =>
    dispatch(brandProfileActions.getBrandLocationsByBrandId(brandId)),
  getBrandId: (brandId) =>
    dispatch(individualStoreProfileActions.getBrandId(brandId)),
  resetRestaurantProfile: () =>
    dispatch(individualStoreProfileActions.resetRestaurantProfile()),
});

export default connect(mapStateToProps, mapDispatchToProps)(BrandProfile);
